import { Box, Flex, Text } from "@chakra-ui/react";
import { forwardRef, type ReactNode } from "react";

/**
 * SegmentedSetting — a label/hint row with an Off/On segmented pill on the
 * right. Used for consequential booleans (auth, master gates, billing toggles)
 * where a tiny Switch under-communicates the weight of the decision.
 *
 * Both segments are always visible; the active one fills with brand colour
 * (`On`) or foreground colour (`Off`), so the current state reads at a glance.
 *
 * Theme-aware via `--bg-primary`, `--border-default`, `--fg-*` tokens — works
 * in light + dark modes without callers passing colour overrides.
 */
export interface SegmentedSettingProps {
  label: string;
  /** Optional helper text under the label. */
  hint?: ReactNode;
  value: boolean;
  onChange: (next: boolean) => void;
  /** When true, both buttons are non-interactive (greyed). */
  disabled?: boolean;
  /** Override the Off / On segment labels (e.g. for localised strings). */
  offLabel?: string;
  onLabel?: string;
}

export const SegmentedSetting = forwardRef<
  HTMLDivElement,
  SegmentedSettingProps
>(function SegmentedSetting(
  { label, hint, value, onChange, disabled, offLabel = "Off", onLabel = "On" },
  ref,
) {
  return (
    <Flex
      ref={ref}
      align="flex-start"
      justify="space-between"
      gap={4}
      p={4}
      borderWidth="1px"
      borderColor="var(--border-default)"
      borderRadius="lg"
      bg="var(--bg-primary)"
      opacity={disabled ? 0.55 : 1}
    >
      <Box minW={0} flex={1}>
        <Text fontSize="sm" fontWeight={500} color="var(--text-primary)" m={0}>
          {label}
        </Text>
        {hint && (
          <Text
            fontSize="xs"
            color="var(--text-secondary-color)"
            mt={1}
            lineHeight="1.5"
            maxW="720px"
          >
            {hint}
          </Text>
        )}
      </Box>
      <Box
        display="inline-flex"
        p="3px"
        borderRadius="full"
        bg="var(--bg-secondary)"
        borderWidth="1px"
        borderColor="var(--border-default)"
        flexShrink={0}
        pointerEvents={disabled ? "none" : "auto"}
      >
        <SegmentButton
          active={!value}
          kind="off"
          onClick={() => onChange(false)}
          disabled={disabled}
        >
          {offLabel}
        </SegmentButton>
        <SegmentButton
          active={value}
          kind="on"
          onClick={() => onChange(true)}
          disabled={disabled}
        >
          {onLabel}
        </SegmentButton>
      </Box>
    </Flex>
  );
});

interface SegmentButtonProps {
  active: boolean;
  kind: "off" | "on";
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}

const SegmentButton: React.FC<SegmentButtonProps> = ({
  active,
  kind,
  onClick,
  disabled,
  children,
}) => {
  // Active On  → brand blue background, white text.
  // Active Off → page foreground background (dark in light, white in dark),
  //              inverted to bg-primary text colour for crisp negative space.
  // Inactive   → transparent, muted text.
  const styles = active
    ? kind === "on"
      ? {
          background: "var(--brand-primary, #3B82F6)",
          color: "#FFFFFF",
        }
      : {
          background: "var(--text-primary)",
          color: "var(--bg-primary)",
        }
    : { background: "transparent", color: "var(--text-secondary-color)" };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "5px 14px",
        borderRadius: 9999,
        border: "none",
        fontFamily: "var(--font-body)",
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: "0.02em",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "background-color 0.15s, color 0.15s",
        ...styles,
      }}
    >
      {children}
    </button>
  );
};

export default SegmentedSetting;
